import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { fetchTripDetail, type TripDetail, type TripDetailItineraryItem } from "@/features/map/layers/useTripItemsLayer";

const ICON_BY_SUBTYPE: Record<string, string> = {
    flight:           "/assets/mapicons/flight_transparent.png",
    taxi:             "/assets/mapicons/taxi_transparent.png",
    ferry:            "/assets/mapicons/ferry_transparent.png",
    train:            "/assets/mapicons/train_transparent.png",
    rental:           "/assets/mapicons/car_rental_transparent.png",
    private_car:      "/assets/mapicons/car_transparent.png",
    bus:              "/assets/mapicons/bus_transparent.png",
    hotel:            "/assets/mapicons/hotel_transparent.png",
    private_property: "/assets/mapicons/house_transparent.png",
    home:             "/assets/mapicons/house_transparent.png",
};

const DEFAULT_ICON = "/assets/mapicons/unknown_transparent.png";
const PAGE_SIZE = 4;

function formatSubType(subType: string): string {
    return subType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
    });
}

function itineraryItemKey(item: TripDetailItineraryItem): string {
    return `${item.type}:${item.subType}:${item.startTime}`;
}

function formatPhone(raw: string): string {
    const digits = raw.replace(/\D/g, "");
    if (raw.startsWith("+1") && digits.length === 11) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (raw.startsWith("+44") && digits.length === 12) {
        return `+44 ${digits.slice(2, 6)} ${digits.slice(6)}`;
    }
    return raw;
}

function camelToWords(s: string): string {
    return s.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();
}

type Props = {
    tripID: string | null;
    selectedItemKey: string;
    open: boolean;
    setOpen: (open: boolean) => void;
    isSuperAdmin?: boolean;
};

export function TripDetailsModal({ tripID, selectedItemKey, open, setOpen, isSuperAdmin }: Props) {
    const [detail, setDetail] = useState<TripDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const selectedRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open || !tripID) return;
        setLoading(true);
        setError(null);
        setDetail(null);
        fetchTripDetail(tripID)
            .then((d) => {
                setDetail(d);
                const idx = d.itinerary.findIndex((it) => itineraryItemKey(it) === selectedItemKey);
                setPage(idx >= 0 ? Math.floor(idx / PAGE_SIZE) + 1 : 1);
            })
            .catch(() => setError("Failed to load trip details."))
            .finally(() => setLoading(false));
    }, [open, tripID, selectedItemKey]);

    useEffect(() => {
        if (detail && selectedRef.current) {
            selectedRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [detail, page]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    if (!open) return null;

    const portal = document.getElementById("portal") ?? document.body;
    const itinerary = detail?.itinerary ?? [];
    const totalPages = Math.ceil(itinerary.length / PAGE_SIZE);
    const pageItems = itinerary.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return ReactDOM.createPortal(
        <div
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-50"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="flex flex-col overflow-hidden rounded-[14px] shadow-2xl bg-white w-[580px] max-w-[95vw] max-h-[90vh]"
            >
                {/* Header */}
                <div className="flex-shrink-0 px-5 pt-4 pb-3">
                    <div className="flex items-center justify-between">
                        <span className="text-base font-bold text-black">Trip Details</span>
                        <button
                            onClick={() => setOpen(false)}
                            className="text-light-grey text-xl leading-none bg-transparent border-none cursor-pointer p-0"
                        >×</button>
                    </div>

                    {detail && (
                        <div className="mt-2.5">
                            <div className="text-sm font-semibold text-dark-dark-grey">
                                {detail.user.firstName} {detail.user.lastName}
                            </div>
                            <a href={`mailto:${detail.user.email}`} className="block text-xs text-blue mt-0.5 no-underline hover:underline">
                                {detail.user.email}
                            </a>
                            {detail.user.phoneNumber && (
                                <a href={`tel:${detail.user.phoneNumber}`} className="block text-xs text-blue no-underline hover:underline">
                                    {formatPhone(detail.user.phoneNumber)}
                                </a>
                            )}
                            {isSuperAdmin && detail.clientName && (
                                <div className="text-xs text-grey">{detail.clientName}</div>
                            )}
                            <div className="text-[11px] text-light-grey mt-1.5">
                                {formatDate(detail.startTime)} – {formatDate(detail.endTime)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1">
                    {loading && (
                        <div className="p-6 text-center text-light-grey text-xs">Loading…</div>
                    )}
                    {error && (
                        <div className="p-6 text-center text-red text-xs">{error}</div>
                    )}

                    {detail && pageItems.map((item, idx) => {
                        const key = itineraryItemKey(item);
                        const isSelected = key === selectedItemKey;
                        const icon = ICON_BY_SUBTYPE[item.subType] ?? DEFAULT_ICON;
                        const loc0 = item.locations[0];
                        const loc1 = item.locations[1];
                        const isLast = idx === pageItems.length - 1;

                        return (
                            <div
                                key={`${key}-${idx}`}
                                ref={isSelected ? selectedRef : undefined}
                                className={[
                                    "flex gap-3 px-5 py-3",
                                    isLast ? "" : "border-b border-light-light-grey",
                                    isSelected ? "bg-glacier-grey border-l-[3px] border-l-blue" : "bg-ice border-l-[3px] border-l-grey",
                                ].join(" ")}
                            >
                                <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full bg-blue flex items-center justify-center">
                                    <img src={icon} alt={item.subType} className="w-5 h-5 object-contain" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-semibold text-dark-dark-grey mb-0.5">
                                        {formatSubType(item.subType)}
                                    </div>

                                    {loc1 ? (
                                        <div className="flex flex-col mb-0.5">
                                            <span className="text-xs text-grey">{loc0?.formatted}</span>
                                            <span className="text-[11px] text-light-grey">{formatDate(item.startTime)}</span>
                                            <span className="text-blue text-[11px] leading-none my-0.5">↓</span>
                                            <span className="text-xs text-grey">{loc1.formatted}</span>
                                            <span className="text-[11px] text-light-grey">{formatDate(item.endTime)}</span>
                                        </div>
                                    ) : loc0 ? (
                                        <div className="text-xs text-grey mb-0.5">
                                            {loc0.formatted}
                                        </div>
                                    ) : null}

                                    {!loc1 && (
                                        <div className="text-[11px] text-light-grey">
                                            {formatDate(item.startTime)} – {formatDate(item.endTime)}
                                        </div>
                                    )}

                                    {item.details && Object.entries(item.details).filter(([, v]) => v).length > 0 && (
                                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                            {Object.entries(item.details)
                                                .filter(([, v]) => v)
                                                .map(([k, v]) => (
                                                    <span key={k} className="text-[11px] text-grey">
                                                        <span className="font-medium">{camelToWords(k)}:</span>{" "}{v}
                                                    </span>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                {detail && (totalPages > 1 || tripID) && (
                    <div className="flex items-center justify-between px-5 py-2.5">
                        {totalPages > 1 ? (
                            <div className="flex items-center gap-3">
                                <button
                                    disabled={page === 1}
                                    onClick={() => setPage((p) => p - 1)}
                                    className={`text-xs bg-transparent border-none p-0 ${page === 1 ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                                >
                                    ← Prev
                                </button>
                                <span className="text-[11px] text-light-grey">
                                    {page} / {totalPages} · {itinerary.length} items
                                </span>
                                <button
                                    disabled={page === totalPages}
                                    onClick={() => setPage((p) => p + 1)}
                                    className={`text-xs bg-transparent border-none p-0 ${page === totalPages ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                                >
                                    Next →
                                </button>
                            </div>
                        ) : (
                            <span className="text-[11px] text-light-grey">
                                {itinerary.length} item{itinerary.length !== 1 ? "s" : ""}
                            </span>
                        )}

                        {tripID && (
                            <div className="flex gap-3">
                                <a
                                    href={`/world-search?tripID=${tripID}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-semibold text-blue no-underline"
                                >
                                    World Search ↗
                                </a>
                                <a
                                    href={`/trips?id=${tripID}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-semibold text-blue no-underline"
                                >
                                    Show Trip ↗
                                </a>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>,
        portal,
    );
}
